import { StrictMode, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntApp, ConfigProvider, theme } from 'antd';
import { EventType, AccountInfo } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import msalInstance, { msalInitPromise } from './msal';
import { recordSessionStart } from './utils/sessionGuard';
import { colors } from './styles/colors';
import { installStyleNonce } from './utils/styleNonce';

const cspNonce =
  document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content || undefined;

// Stamp the CSP nonce onto every dynamically-created <style> (e.g. rc-util's
// scroll-lock) so they aren't blocked under a strict, nonce-based style-src.
// Must run before any dependency injects styles.
installStyleNonce(cspNonce);

const Fallback = () => <div className="p-8 text-center">Loading...</div>;

// Restore an active account on initial load if one is cached but not set
function useRestoreMsalActiveAccount() {
  useEffect(() => {
    const active = msalInstance.getActiveAccount();
    if (!active) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 1) {
        msalInstance.setActiveAccount(accounts[0]);
      } else if (accounts.length > 1) {
        // Optionally implement a selection strategy; for now pick first
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  }, []);
}

// Listen for login / token success to set active account automatically
msalInstance.addEventCallback((event) => {
  if (import.meta.env.DEV) {
    const err = (event as any).error;
    // eslint-disable-next-line no-console
    console.log('[MSAL EVENT]', event.eventType, {
      interactionType: (event as any).interactionType,
      errorCode: err?.errorCode ?? null,
      subError: err?.subError ?? null,
    });
  }
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    const account = (event.payload as any)?.account as AccountInfo | undefined;
    if (account) msalInstance.setActiveAccount(account);
    recordSessionStart(true);
  }
  if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
    const account = (event.payload as any)?.account as AccountInfo | undefined;
    if (account) msalInstance.setActiveAccount(account);
  }
});

const queryClient = new QueryClient();

const RootProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useRestoreMsalActiveAccount();
  return (
    <MsalProvider instance={msalInstance}>
      <ConfigProvider
        csp={cspNonce ? { nonce: cspNonce } : undefined}
        theme={{
          cssVar: false,
          algorithm: [theme.darkAlgorithm],
          token: {
            colorPrimary: colors.brandGold,
            colorBgBase: colors.pageBg,
            colorBgContainer: colors.cardBg,
            colorBorderSecondary: 'inherit',
            colorTextLightSolid: 'black',
            colorBgSpotlight: colors.brandGold,
            colorBorder: colors.cardBorder,
            colorSplit: colors.cardBorder,
          },
          components: {
            Table: {
              borderColor: colors.cardBorder,
              rowHoverBg: colors.rowHoverBg,
              rowSelectedBg: colors.rowHoverBg,
              rowSelectedHoverBg: colors.rowHoverBg,
            },
            Message: { contentBg: colors.messageBg },
            Modal: { contentBg: colors.cardBg, headerBg: colors.cardBg },
          },
        }}>
        <AntApp>
          <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </AntApp>
      </ConfigProvider>
    </MsalProvider>
  );
};

// React Query Devtools removed (dependency not installed). To enable later: install package and add lazy import here.

msalInitPromise
  .catch((err) => {
    console.error('MSAL initialization failed, rendering app without auth state', err);
  })
  .finally(() => {
    createRoot(document.getElementById('root') as HTMLElement).render(
      <StrictMode>
        <Suspense fallback={<Fallback />}>
          <RootProviders>
            <App />
          </RootProviders>
        </Suspense>
      </StrictMode>,
    );
  });
